/**
 * Money arrives from the API as integer rupiah. The web never does money arithmetic of its own;
 * totals come from the API (ADR-0011 clause 4). A non-integer amount is a bug, never rounded away.
 */
export function formatRupiah(value: number, withPrefix = true): string {
  if (!Number.isSafeInteger(value)) throw new Error(`not an integer rupiah amount: ${value}`)
  const sign = value < 0 ? '-' : ''
  const body = Math.abs(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return withPrefix ? `${sign}Rp ${body}` : `${sign}${body}`
}

/** 1000 → "10", 250 → "2,5", 1250 → "12,5", 125 → "1,25" (Indonesian decimal comma). */
export function formatPercent(bps: number): string {
  const whole = Math.trunc(bps / 100)
  const frac = String(bps % 100).padStart(2, '0').replace(/0+$/, '')
  return frac === '' ? String(whole) : `${whole},${frac}`
}

/** The most the server takes for a price, a cost or a fixed commission. */
export const MAX_RUPIAH = 1_000_000_000

/** Whole rupiah from what was typed ("35.000", "35000"); null when empty. Never a float. */
export function parseRupiah(text: string): number | null {
  const digits = text.replace(/\D/g, '')
  return digits === '' ? null : Number(digits)
}

/** "40" → 4000 bps, "12,5" → 1250; null when not a percentage with at most 2 decimals, ≤ 100. */
export function parsePercentBps(text: string): number | null {
  const m = /^(\d{1,3})(?:[.,](\d{1,2}))?$/.exec(text.trim())
  if (!m) return null
  const bps = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0') || 0)
  return bps <= 10_000 ? bps : null
}
